<template>
    <div class="fill-height pa-2">
        <v-card class="fill-height d-flex flex-column of-y" style="max-height: 100%; position: relative; ">
            <v-progress-linear v-if="showLoading" absolute indeterminate></v-progress-linear>
            <v-card-title class="pa-0 ma-0 d-flex align-center">
                <div class="dlg-title-bar flex-grow-1">
                    {{ selectedUser.name }}
                </div>
            </v-card-title>

            <app-divider :label="`User Options${selectedUser.adminSiteId !== $store.state.curSiteId ? ' (Limited Access)' : ''}`" show-expanded :expanded.sync="showUserOptions"/>

            <v-scroll-y-transition hide-on-leave>
                <div v-if="showUserOptions" class="pb-2">
                    <template v-if="selectedUser.adminSiteId === $store.state.curSiteId">
                        <v-form ref="modifyForm" @submit="$event.preventDefault()">
                            <div class="d-flex flex-column px-6">
                                <div class="pt-3 d-flex align-center justify-center">
                                    <v-text-field
                                        :disabled="curUserLimitations.includes('uoan')"
                                            color="primary"
                                            v-model="editUser.name"
                                            label="Account Name"
                                            outlined
                                            dense
                                            :rules="[validationRules.required]"
                                            hide-details="auto"
                                            @input="editUserChangesMade = true"
                                    >
                                    </v-text-field>
                                </div>
                                <div class="pt-4 d-flex align-center justify-center">
                                    <v-text-field
                                        :disabled="curUserLimitations.includes('uoun')"
                                            color="primary"
                                            outlined
                                            v-model="editUser.username"
                                            dense
                                            label="Username"
                                            placeholder="eg. person@domain.com"
                                            hide-details="auto"
                                            :hint="editUserUsernameErrors"
                                            :error-messages="editUserUsernameErrors !== null ? [editUserUsernameErrors] : null"
                                            @input="editUserChangesMade = true; editUserUsernameErrors = null"
                                            :rules="[validationRules.required]"
                                    >
                                    </v-text-field>
                                </div>

                                <div class="pt-4 d-flex align-center justify-center">
                                    <v-text-field
                                        :disabled="curUserLimitations.includes('uop')"
                                            ref="password"
                                            color="primary"
                                            outlined
                                            v-model="editUser.password"
                                            dense
                                            label="Password"
                                            type="password"
                                            placeholder="Password"
                                            hide-details="auto"
                                            autocomplete="new-password"
                                            :rules="editUser.password ? [validationRules.required, validationRules.upperCase, validationRules.lowerCase, validationRules.numeric, validationRules.specialCharacter, validationRules.min6Characters] : []"
                                            @input="editUserChangesMade = true"
                                            class="flex-grow-1"
                                    >
                                    </v-text-field>
                                    <v-btn
                                            outlined
                                            small
                                            color="blue"
                                            class="my-1 ml-2"
                                            :loading="sendingPasswordReset"
                                            :disabled="sendingPasswordReset || curUserLimitations.includes('uopl')"
                                            @click="sendPasswordReset"
                                    >
                                        <v-icon left>mdi-send-lock</v-icon>
                                        Send Reset Link
                                    </v-btn>
                                </div>

                                <div class="pt-2 d-flex align-center justify-space-between">
                                    <div class="d-flex align-center flex-grow-1">
                                        <v-icon :color="selectedUser.totp.enabled ? null : 'error'">
                                            {{ selectedUser.totp.enabled ? 'mdi-qrcode' : 'mdi-qrcode-remove' }}
                                        </v-icon>
                                        <div class="body-1 px-3 py-2"
                                             :class="selectedUser.totp.enabled ? null : 'error--text'">
                                            TOTP Login {{ selectedUser.totp.enabled ? 'Enabled' : 'Disabled' }}
                                        </div>
                                    </div>

                                    <v-btn
                                            outlined
                                            small
                                            color="blue"
                                            :loading="resettingTotp"
                                            :disabled="resettingTotp || curUserLimitations.includes('uoto')"
                                            @click="resetTotp"
                                            class="mr-2"
                                            v-if="selectedUser.totp.enabled"
                                    >
                                        <v-icon left>mdi-lock-reset</v-icon>
                                        Reset TOTP
                                    </v-btn>

                                    <v-btn
                                            outlined
                                            small
                                            :color="selectedUser.totp.enabled ? 'error' : 'green'"
                                            :loading="enablingTOTP"
                                            :disabled="enablingTOTP || curUserLimitations.includes('uoto')"
                                            @click="enableTOTP"
                                    >
                                        <v-icon left>
                                            {{ !selectedUser.totp.enabled ? 'mdi-qrcode' : 'mdi-qrcode-remove' }}
                                        </v-icon>
                                        {{ !selectedUser.totp.enabled ? 'Enable' : 'Disable' }}
                                    </v-btn>
                                </div>
                            </div>

                            <div class="px-6 d-flex align-center justify-space-between">
                                <div class="d-flex align-center flex-grow-1">
                                    <v-icon :color="userEnabled ? null : 'error'">
                                        {{ userEnabled ? 'mdi-lock' : 'mdi-lock-off' }}
                                    </v-icon>
                                    <div class="body-1 px-3 py-2" :class="userEnabled ? null : 'error--text'">
                                        User {{ userEnabled ? 'Enabled' : 'Disabled' }}
                                    </div>
                                </div>

                                <v-btn
                                        outlined
                                        small
                                        :color="userEnabled ? 'error' : 'green'"
                                        :loading="disablingUser"
                                        :disabled="disablingUser || curUserLimitations.includes('uoen')"
                                        @click="disableUser"
                                >
                                    <v-icon left>
                                        {{ !userEnabled ? 'mdi-lock' : 'mdi-lock-off' }}
                                    </v-icon>
                                    {{ !userEnabled ? 'Enable' : 'Disable' }}
                                </v-btn>
                            </div>

                            <div class="px-3 pt-4 d-flex justify-end">
                                <v-btn
                                        ref="passwordBtn"
                                        :disabled="showLoading"
                                        color="primary"
                                        @click="resetValue"
                                        small
                                        outlined
                                        class="mr-2"
                                >
                                    Cancel
                                </v-btn>

                                <v-btn
                                        ref="passwordBtn"
                                        :disabled="!editUserChangesMade || showLoading"
                                        :loading="showLoading"
                                        color="primary"
                                        @click="saveChanges"
                                        small
                                >
                                    Save Changes
                                </v-btn>
                            </div>
                        </v-form>


                    </template>
                    <template v-else>

                        <div class="body-2 grey--text overline text-center">
                            Account is managed by another site.
                        </div>


                        <div class="px-6 d-flex align-center justify-space-between">
                            <div class="d-flex align-center flex-grow-1">
                                <v-icon :color="userEnabled ? null : 'error'">
                                    {{ userEnabled ? 'mdi-lock' : 'mdi-lock-off' }}
                                </v-icon>
                                <div class="body-1 px-3 py-2" :class="userEnabled ? null : 'error--text'">
                                    User {{ userEnabled ? 'Enabled' : 'Disabled' }}
                                </div>
                            </div>

                            <v-btn
                                    outlined
                                    small
                                    :color="userEnabled ? 'error' : 'green'"
                                    :loading="disablingUser"
                                    :disabled="disablingUser"
                                    @click="disableUser"
                            >
                                <v-icon left>
                                    {{ !userEnabled ? 'mdi-lock' : 'mdi-lock-off' }}
                                </v-icon>
                                {{ !userEnabled ? 'Enable' : 'Disable' }}
                            </v-btn>
                        </div>
                    </template>

                </div>
            </v-scroll-y-transition>

            <!--User Prefs-->
            <app-divider label="User Access" show-expanded :expanded.sync="showUserPrefs"></app-divider>

            <v-scroll-y-transition hide-on-leave>
                <div v-if="showUserPrefs">
                    <app-user-site-pref-options :user-id="selectedUser._id"></app-user-site-pref-options>
                </div>
            </v-scroll-y-transition>
<!--Teams-->
            <app-divider
                    label="Limit To Teams"
                    :show-search="showTeams"
                    :search.sync="teamsFilterStr"
                    v-if="!curUserLimitations.includes('lt')"
            >
                <template v-slot:action>
                    <v-tooltip top>
                        <template v-slot:activator="{on}">
                            <v-btn
                                :disabled="curUserLimitations.includes('lta')"
                                    dark
                                    color="primary"
                                    icon
                                    v-on="on"
                                    @click="showAddTeams = true"
                            >
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        Add Teams
                    </v-tooltip>
                </template>
            </app-divider>

            <v-scroll-y-transition hide-on-leave>
                <v-list
                        v-if="showTeams"
                        dense
                        class="mt-0 pt-0 flex-grow-1 of-y"
                        style="padding-bottom: 32px;"
                >
                    <v-list-item
                            v-for="team in teamsByUserSorted"
                            :key="team._id"
                    >
                        <v-list-item-avatar>
                            <v-icon>mdi-account-group</v-icon>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-title v-text="team.name"/>
                        </v-list-item-content>


                        <v-list-item-action>
                            <div class="d-flex align-center mr-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{on}">
                                        <v-btn
                                                icon
                                                color="error"
                                                :loading="loadingTeams.includes(team._id)"
                                                :disabled="loadingTeams.includes(team._id) || curUserLimitations.includes('ltr')"
                                                v-on="on"
                                                @click="removeTeam(team)"
                                        >
                                            <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                    </template>
                                    Remove Team
                                </v-tooltip>
                            </div>

                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </v-scroll-y-transition>

<!--Add Teams-->
            <app-dlg
                    v-if="showAddTeams"
                    :show-dlg.sync="showAddTeams"
                    title="Add Teams"
                    :default-padding="false"
            >

                <app-list
                        v-model="addTeamIds"
                        :items="teams.filter(team => !(team.userIds || []).includes(selectedUser._id)).sort(nameSort)"
                        :multiple="true"
                        headline-key="name"
                        item-key="_id"
                        :show-check-boxes="true"
                        :return-object="false"
                        class="fill-height of-y"
                        :height="400"
                />

                <template v-slot:actions>
                    <v-btn
                            class="ml-2"
                            small
                            color="primary"
                            @click="showAddTeams = false"
                            outlined
                            size="small"
                            :disabled="addingTeams"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                            @click="addTeamsToUser"
                            small
                            class="ml-2"
                            color="secondary"
                            variant="contained"
                            size="small"
                            :disabled="addingTeams || !addTeamIds || addTeamIds.length === 0"
                            :loading="addingTeams"
                    >
                        {{ addTeamIds.length === 1 ? 'Add Team' : 'Add Teams' }}
                    </v-btn>
                </template>
            </app-dlg>


        </v-card>
    </div>
</template>

<script>

import {User} from "@/models/user";
import AppList from "@/components/Shared/AppList/AppList";
import AppDlg from "@/components/Shared/AppDlg";
import {arrSplice, nameSort, validationRules} from "@/core/codeFunctions";
import {mapGetters} from "vuex";
import AppDivider from "@/components/Shared/AppDivider/AppDivider";
import {Device} from "@/models/device";
import {Team} from "@/models/team";
import AppUserSitePrefOptions from "@/components/Users/AppUserSitePrefOptions";

export default {
    components: {AppUserSitePrefOptions, AppDivider, AppDlg, AppList},
    props: {
        selectedUser: User
    },
    data() {
        return {
            showUserPrefs: true,

            showLoading: false,
            fab: false,
            validationRules: validationRules,
            showUserOptions: true,
            showSecurityOptions: true,
            nameSort: nameSort,

            editUser: new User({}),
            editUserChangesMade: false,
            editUserUsernameErrors: null,

            sendingPasswordReset: false,
            resettingTotp: false,
            enablingTOTP: false,
            disablingUser: false,

            showTeams: true,
            teamsFilterStr: null,
            loadingTeams: [],

            showAddTeams: false,
            addTeamIds: [],
            addingTeams: false
        }
    },
    watch: {

        "selectedUser": {
            immediate: true,
            handler(val) {
                this.editUser = new User(JSON.parse(JSON.stringify(this.selectedUser)))
            }
        },

        removeTeam(team) {

        }
    },
    methods: {

        async removeTeam(team) {
            this.loadingTeams.push(team._id)
            try {
                const tTeam = new Team(JSON.parse(JSON.stringify(team)))
                team.once('update', () => {
                    arrSplice(this.loadingTeams, (teamId) => team._id === teamId)
                })
                arrSplice(tTeam.userIds, (userId) => userId === this.selectedUser._id)
                await tTeam.update('userIds')
            } catch (e) {
                console.log(e)
                this.$snack.netWorkError()
                this.renamingTeam = false
            }
        },

        async disableUser() {
            try {
                // Build The Update Object
                const updateKeys = ['disabledSiteIds'];

                if (!this.editUser.disabledSiteIds.includes(this.$store.state.curSiteId)) {
                    this.editUser.disabledSiteIds.push(this.$store.state.curSiteId)
                } else {
                    arrSplice(this.editUser.disabledSiteIds, (id) => id === this.$store.state.curSiteId)
                }

                this.disablingUser = true;

                this.selectedUser.once('update', (payload) => {
                    this.disablingUser = false;
                    this.$emit('checkSelected')
                })

                const res = await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.disablingUser = false;
            }
        },

        async resetTotp() {
            try {
                this.resettingTotp = true;
                const res = await this.$newReq('POST', 'users/resetTotp', {_id: this.selectedUser._id})
                this.resettingTotp = false;
                this.$snack.info('TOTP reset')
            } catch (e) {
                console.log(e)
                this.resettingTotp = false;
            }
        },

        async enableTOTP() {
            try {
                // Build The Update Object
                const updateKeys = ['totp'];
                this.editUser.totp.enabled = !this.editUser.totp.enabled

                this.enablingTOTP = true;
                this.selectedUser.once('update', (payload) => {
                    this.enablingTOTP = false;
                })

                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.enablingTOTP = false;
            }
        },

        async saveChanges() {
            try {

                if (!this.$refs.modifyForm.validate())
                    return this.$snack.error('Please Complete All Fields')

                // Check Username Duplicate
                const {duplicates} = await this.$newReq('POST', 'users/checkUsernameModify', {
                    _id: this.editUser._id,
                    username: this.editUser.username
                })
                if (duplicates)
                    this.usernameErrors = 'Username Already Taken'

                // Build The Update Object
                const updateKeys = ['name', 'username', 'totp', 'disabledSiteIds'];
                if (this.editUser.password)
                    updateKeys.push("password")

                this.showLoading = true;
                this.selectedUser.once('update', (payload) => {
                    this.showLoading = false;
                })

                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.showLoading = false;
            }
        },

        resetValue() {
            this.editUser = new User(JSON.parse(JSON.stringify(this.selectedUser)));
            if (this.$refs.modifyForm)
                this.$refs.modifyForm.resetValidation();
            this.editUserUsernameErrors = null
            this.showLoading = false;
            this.editUserChangesMade = false;
            this.resettingTotp = false;
            this.sendingPasswordReset = false;
        },

        async sendPasswordReset() {
            try {
                this.sendingPasswordReset = true;
                const res = await this.$newReq('POST', 'users/passwordResetInvite', {_id: this.selectedUser._id})
                this.sendingPasswordReset = false;
                this.$snack.info('Password reset invite sent')
            } catch (e) {
                console.log(e)
                this.sendingPasswordReset = false;
            }
        },


        async addTeamsToUser() {
            try {
                const res = await this.$newReq('POST', `users/addTeams`, {
                    userId: this.selectedUser._id,
                    teamIds: this.addTeamIds
                })
                this.addTeamIds = [];
                this.showAddTeams = false;
            } catch (e) {
                this.$snack.netWorkError();
                this.addingTeams = false;
            } finally {
                this.addingTeams = false
            }
        },

    },
    computed: {
        ...mapGetters(['users', 'teamsRef', 'teams', 'teams', 'teamsByUser', 'usersSitePrefs', 'usersSitePrefsRef', 'user']),

        userEnabled() {
            return !this.selectedUser.disabledSiteIds.includes(this.$store.state.curSiteId)
        },

        curUserLimitations() {
            return this.usersSitePrefsRef[this.user._id] && this.usersSitePrefsRef[this.user._id].userRestrictions ? this.usersSitePrefsRef[this.user._id].userRestrictions : []
        },

        // team() {
        //     return this.selectedUser.teamId && this.teamsRef[this.selectedUser.teamId] ? this.teamsRef[this.selectedUser.teamId] : null
        // },
        // availableUsers() {
        //     return this.users.filter((emp) => !emp.disabled).sort(nameSort)
        // },
        // availableTeams() {
        //     return [...this.teams].sort(nameSort)
        // },
        teamsByUserSorted() {
            if (!this.teamsByUser[this.selectedUser._id])
                return []
            return [...this.teamsByUser[this.selectedUser._id]]
                    .filter(team => this.teamsFilterStr ? JSON.stringify(team).toLowerCase().includes((this.teamsFilterStr || '').toLowerCase()) : true)
                    .sort(nameSort)
        }

    }

}
</script>

<style scoped>

</style>